import { toIntlLocale } from 'shared/helpers/intlLocale';

const EMPTY_TEXT = 'Sem preenchimento';

export const CARD_TEMPLATE_ICONS = Object.freeze({
  text: 'i-ri-text',
  description: 'i-ri-file-text-line',
  flag: 'i-ri-flag-2-line',
  money: 'i-ri-money-dollar-circle-line',
  calendar: 'i-ri-calendar-line',
  'map-pin': 'i-ri-map-pin-line',
  chat: 'i-ri-chat-3-line',
  tags: 'i-ri-price-tag-3-line',
  user: 'i-ri-user-line',
  checklist: 'i-ri-list-check-3',
  source: 'i-ri-compass-3-line',
  building: 'i-ri-building-2-line',
  phone: 'i-ri-phone-line',
  mail: 'i-ri-mail-line',
  hash: 'i-ri-hashtag',
  star: 'i-ri-star-line',
});

export const EMPTY_CARD_TEMPLATE_DEMO = Object.freeze({
  id: 14,
  funnel_stage: '',
  assigned_agents: [],
  checklist: [],
  attachments: 0,
  item_details: {},
  conversation: null,
});

export const FILLED_CARD_TEMPLATE_DEMO = Object.freeze({
  id: 14,
  funnel_stage: 'Aguardando orçamento',
  assigned_agents: [{ id: 1, name: 'Lucas Oliveira' }],
  checklist: [{ completed: true }, { completed: false }],
  attachments: 2,
  item_details: {
    title: 'Pedido de peças',
    description: 'Cliente precisa de orçamento para reposição de duas peças.',
    priority: 'high',
    value: 1890,
    currency: { code: 'BRL', locale: 'pt-BR' },
    scheduled_at: '2026-09-08T14:30:00-03:00',
    source: 'WhatsApp',
    region: 'RS',
    custom_attributes: [
      { name: 'Número da ordem', type: 'text', value: 'OS 014' },
    ],
  },
  conversation: {
    display_id: 1402,
    contact: {
      name: 'Lucas Oliveira',
      email: 'contato@siteup.com.br',
      phone_number: '+55 51 98976-9026',
    },
    labels: ['assistência', 'orçamento'],
    last_message: { content: 'Pode me enviar o valor ainda hoje?' },
  },
});

const valueFromCustomAttributes = (item, key) => {
  const attributes = item?.item_details?.custom_attributes;
  if (Array.isArray(attributes)) {
    return attributes.find(attribute => attribute?.name === key)?.value;
  }
  return attributes?.[key];
};

const valueFromCard = (item, key) => {
  if (key === 'assigned_agents') {
    return (item?.assigned_agents || [])
      .map(agent => agent.name)
      .filter(Boolean);
  }
  if (key === 'checklist') {
    const entries = Array.isArray(item?.checklist) ? item.checklist : [];
    const completed = entries.filter(entry => entry?.completed).length;
    return entries.length ? `${completed}/${entries.length}` : null;
  }
  if (key === 'attachments') {
    return Array.isArray(item?.attachments)
      ? item.attachments.length
      : item?.attachments;
  }
  if (key === 'notes_count') {
    const notes = item?.item_details?.notes;
    return Array.isArray(notes) ? notes.length : notes;
  }
  return item?.[key];
};

const valueFromConversation = (item, key) => {
  const conversation = item?.conversation;
  const contact = conversation?.contact;
  const values = {
    display_id: conversation?.display_id,
    contact_name: contact?.name,
    contact_email: contact?.email,
    contact_phone: contact?.phone_number,
    labels: conversation?.labels,
    last_message: conversation?.last_message?.content,
  };
  return values[key];
};

export const resolveTemplateFieldValue = (item, field) => {
  if (!item || !field) return null;
  if (field.source === 'custom_attribute') {
    return valueFromCustomAttributes(item, field.key);
  }
  if (field.source === 'card') return valueFromCard(item, field.key);
  if (field.source === 'conversation') {
    return valueFromConversation(item, field.key);
  }
  return item?.item_details?.[field.key];
};

export const isTemplateValueEmpty = value => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === 'string' ? value.trim() === '' : false;
};

const formatDate = value => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: value.toString().includes('T') ? 'short' : undefined,
  }).format(date);
};

export const formatTemplateFieldValue = (value, field, item) => {
  if (isTemplateValueEmpty(value)) return EMPTY_TEXT;
  if (field.key === 'value' && Number.isFinite(Number(value))) {
    const currency = item?.item_details?.currency || {};
    return new Intl.NumberFormat(toIntlLocale(currency.locale || 'pt-BR'), {
      style: 'currency',
      currency: currency.code || 'BRL',
    }).format(Number(value));
  }
  if (field.key.endsWith('_at')) return formatDate(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
};

export const fieldsForTemplateSurface = ({
  item,
  presentation,
  surface,
  preview = false,
}) => {
  const fields = presentation?.[surface]?.fields;
  if (!Array.isArray(fields)) return [];

  const sharedRecipient = item?.access?.shared === true;
  const hiddenForSharedRecipient = field =>
    field?.source === 'conversation' ||
    (field?.source === 'card' &&
      ['assigned_agents', 'checklist', 'attachments'].includes(field.key)) ||
    (field?.source === 'item_details' && field.key === 'status');

  return fields
    .filter(
      field =>
        field?.visible !== false &&
        (!sharedRecipient || !hiddenForSharedRecipient(field))
    )
    .flatMap(field => {
      const value = resolveTemplateFieldValue(item, field);
      const empty = isTemplateValueEmpty(value);
      if (empty && field.hide_when_empty !== false && !preview) return [];
      return [
        {
          ...field,
          empty,
          displayValue:
            empty && preview && field.hide_when_empty !== false
              ? 'Oculto quando vazio'
              : formatTemplateFieldValue(value, field, item),
        },
      ];
    });
};

export const templateFieldKey = field => `${field.source}:${field.key}`;

export const newTemplateField = definition => ({
  source: definition.source,
  key: definition.key,
  label: definition.label,
  icon: definition.icon || 'text',
  visible: true,
  hide_when_empty: true,
  width: 'full',
  size: 'normal',
  group: 'Informações',
});
